<template>
  <div>
    <!-- Main Dialog -->
    <v-dialog v-model="internalVisible" max-width="800px" persistent>
      <v-card>
        <v-card-title class="headline">
          <v-icon left>mdi-calendar-clock</v-icon>
          班表設定
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-spacer></v-spacer>
            <v-col cols="auto">
              <v-btn
                color="primary"
                elevation="0"
                :ripple="false"
                height="43"
                class="
                  font-weight-bold
                  text-capitalize
                  btn-primary
                  bg-gradient-primary
                  py-3
                  px-3
                  mx-3
                "
                @click="openCreateDialog"
              >
                <v-icon left>mdi-plus</v-icon>
                新增班表
              </v-btn>
            </v-col>
          </v-row>
          <v-list v-if="shiftTypes.length > 0">
            <v-list-item-group>
              <v-list-item v-for="shift in shiftTypes" :key="shift.id">
                <v-list-item-content>
                  <v-list-item-title
                    >{{ shift.code }}:
                    {{ shift.name || "未命名" }}</v-list-item-title
                  >
                  <v-list-item-subtitle>
                    {{
                      shift.clock_in_time
                        ? `${shift.clock_in_time} - ${shift.clock_out_time}`
                        : "無時間設定"
                    }}
                    <span v-if="shift.break_minutes"
                      >, 休息 {{ shift.break_minutes }} 分鐘</span
                    >
                    <span
                      >, 午休 {{ shift.lunch_break_start }} -
                      {{ shift.lunch_break_end }}</span
                    >
                    <span v-if="shift.flexible">, 彈性</span>
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="flex-row">
                  <v-btn icon color="primary" @click="openEditDialog(shift)">
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn icon color="red" @click="confirmDelete(shift.id)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list-item-group>
          </v-list>
          <div v-else class="text-center py-4">
            <v-icon size="48" color="grey">mdi-calendar-blank-outline</v-icon>
            <p class="mt-2">尚無班表設定</p>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="closeDialog">關閉</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Create Dialog -->
    <v-dialog v-model="createDialogVisible" max-width="600px" persistent>
      <v-card>
        <v-card-title>{{ isEditing ? "編輯班表" : "新增班表" }}</v-card-title>
        <v-card-text>
          <v-form ref="createForm">
            <v-text-field
              v-model="newShift.code"
              label="代碼"
              required
              :rules="[(v) => !!v || '代碼為必填']"
            ></v-text-field>
            <v-text-field v-model="newShift.name" label="名稱"></v-text-field>
            <v-row>
              <v-col cols="6">
                <v-menu
                  v-model="timeMenus.start"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="newShift.clock_in_time"
                      label="開始時間"
                      outlined
                      dense
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      @click="timeMenus.start = true"
                    ></v-text-field>
                  </template>
                  <v-time-picker
                    v-if="timeMenus.start"
                    v-model="newShift.clock_in_time"
                    format="24hr"
                    :use-seconds="false"
                    full-width
                    color="blue"
                    @change="onClockInTimeChange"
                  ></v-time-picker>
                </v-menu>
              </v-col>
              <v-col cols="6">
                <v-menu
                  v-model="timeMenus.end"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="newShift.clock_out_time"
                      label="結束時間"
                      outlined
                      dense
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      @click="timeMenus.end = true"
                    ></v-text-field>
                  </template>
                  <v-time-picker
                    v-if="timeMenus.end"
                    v-model="newShift.clock_out_time"
                    format="24hr"
                    :use-seconds="false"
                    full-width
                    color="blue"
                    @change="timeMenus.end = false"
                  ></v-time-picker>
                </v-menu>
              </v-col>
            </v-row>

            <v-checkbox
              v-model="newShift.lunch_break"
              label="午休(休息1) 是否打卡"
            ></v-checkbox>
            <v-row>
              <v-col cols="6">
                <v-menu
                  v-model="timeMenus.lunchStart"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="newShift.lunch_break_start"
                      label="午休開始"
                      outlined
                      dense
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      @click="timeMenus.lunchStart = true"
                    ></v-text-field>
                  </template>
                  <v-time-picker
                    v-if="timeMenus.lunchStart"
                    v-model="newShift.lunch_break_start"
                    format="24hr"
                    :use-seconds="false"
                    full-width
                    color="blue"
                    @change="timeMenus.lunchStart = false"
                  ></v-time-picker>
                </v-menu>
              </v-col>
              <v-col cols="6">
                <v-menu
                  v-model="timeMenus.lunchEnd"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="newShift.lunch_break_end"
                      label="午休結束"
                      outlined
                      dense
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      @click="timeMenus.lunchEnd = true"
                    ></v-text-field>
                  </template>
                  <v-time-picker
                    v-if="timeMenus.lunchEnd"
                    v-model="newShift.lunch_break_end"
                    format="24hr"
                    :use-seconds="false"
                    full-width
                    color="blue"
                    @change="timeMenus.lunchEnd = false"
                  ></v-time-picker>
                </v-menu>
              </v-col>
            </v-row>
            <v-checkbox
              v-model="newShift.rest_1_start_enable"
              label="啟用休息2"
            ></v-checkbox>
            <v-row>
              <v-col cols="6">
                <v-menu
                  v-model="timeMenus.rest1Start"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="newShift.rest_1_start"
                      label="休息2開始"
                      outlined
                      dense
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      @click="timeMenus.rest1Start = true"
                    ></v-text-field>
                  </template>
                  <v-time-picker
                    v-if="timeMenus.rest1Start"
                    v-model="newShift.rest_1_start"
                    format="24hr"
                    :use-seconds="false"
                    full-width
                    color="blue"
                    @change="timeMenus.rest1Start = false"
                  ></v-time-picker>
                </v-menu>
              </v-col>
              <v-col cols="6">
                <v-menu
                  v-model="timeMenus.rest1End"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="newShift.rest_1_end"
                      label="休息2結束"
                      outlined
                      dense
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      @click="timeMenus.rest1End = true"
                    ></v-text-field>
                  </template>
                  <v-time-picker
                    v-if="timeMenus.rest1End"
                    v-model="newShift.rest_1_end"
                    format="24hr"
                    :use-seconds="false"
                    full-width
                    color="blue"
                    @change="timeMenus.rest1End = false"
                  ></v-time-picker>
                </v-menu>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="closeCreateDialog">取消</v-btn>
          <v-btn
            color="primary"
            elevation="0"
            :ripple="false"
            height="43"
            class="
              font-weight-bold
              text-capitalize
              btn-primary
              bg-gradient-primary
              py-3
              px-3
              mx-3
            "
            @click="saveShiftType"
            >{{ isEditing ? "儲存" : "新增" }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialogVisible" max-width="400px">
      <v-card>
        <v-card-title>確認刪除</v-card-title>
        <v-card-text>確定要刪除這個班表嗎？</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            elevation="0"
            :ripple="false"
            height="43"
            class="
              font-weight-bold
              text-capitalize
              btn-default
              bg-gradient-default
              py-3
              px-3
              mx-3
            "
            @click="cancelDelete"
            >取消</v-btn
          >
          <v-btn
            color="primary"
            elevation="0"
            :ripple="false"
            height="43"
            class="
              font-weight-bold
              text-capitalize
              btn-primary
              bg-gradient-primary
              py-3
              px-3
              mx-3
            "
            @click="deleteShiftType"
            >刪除</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Progress Dialog -->
    <progress-dialog :visible="loading" message="載入中..." />

    <v-snackbar
      v-model="snackbar.show"
      :timeout="snackbar.timeout"
      :color="snackbar.color"
      top
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn v-bind="attrs" text @click="snackbar.show = false">關閉</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import ProgressDialog from "@/components/Common/ProgressDialog.vue";

export default {
  name: "ShiftTypeDialog",
  emits: ["created", "deleted"],
  components: {
    ProgressDialog,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    companyId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      internalVisible: this.visible,
      shiftTypes: [],
      loading: false,
      createDialogVisible: false,
      isEditing: false,
      deleteDialogVisible: false,
      deleteId: null,
      timeMenus: {
        start: false,
        end: false,
        lunchStart: false,
        lunchEnd: false,
        rest1Start: false,
        rest1End: false,
      },
      newShift: {
        code: "",
        name: "",
        clock_in_time: "",
        clock_out_time: "",
        break_minutes: null,
        flexible: false,
        lunch_break: false,
        lunch_break_start: null,
        lunch_break_end: null,
        rest_1_start_enable: false,
        rest_1_start: null,
        rest_1_end: null,
        company_id: this.companyId,
      },
      snackbar: {
        show: false,
        text: "",
        color: "success",
        timeout: 3000,
      },
    };
  },
  watch: {
    visible(val) {
      this.internalVisible = val;
      if (val) {
        this.fetchShiftTypes();
      }
    },
    internalVisible(val) {
      if (!val) {
        this.$emit("update:visible", val);
      }
    },
  },
  methods: {
    showSnackbar(text, color = "success") {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    onClockInTimeChange(value) {
      this.timeMenus.start = false;
      if (!value) return;
      this.newShift.clock_in_time = value;
      this.applyRelativeTimesFromClockIn();
    },
    applyRelativeTimesFromClockIn() {
      const baseMinutes = this.parseTimeToMinutes(this.newShift.clock_in_time);
      if (baseMinutes == null) return;
      const nineHours = 9 * 60;
      const fourHours = 4 * 60;
      const fiveHours = 5 * 60;
      this.newShift.clock_out_time = this.formatMinutesToTime(
        baseMinutes + nineHours
      );
      this.newShift.lunch_break_start = this.formatMinutesToTime(
        baseMinutes + fourHours
      );
      this.newShift.lunch_break_end = this.formatMinutesToTime(
        baseMinutes + fiveHours
      );
    },
    parseTimeToMinutes(timeStr) {
      if (!timeStr || typeof timeStr !== "string") return null;
      const [hourStr, minuteStr = "0"] = timeStr.split(":");
      const hours = Number(hourStr);
      const minutes = Number(minuteStr);
      if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
      return hours * 60 + minutes;
    },
    formatMinutesToTime(totalMinutes) {
      if (totalMinutes == null) return "";
      const minutesInDay = 24 * 60;
      let normalized = totalMinutes % minutesInDay;
      if (normalized < 0) normalized += minutesInDay;
      const hours = Math.floor(normalized / 60)
        .toString()
        .padStart(2, "0");
      const minutes = (normalized % 60).toString().padStart(2, "0");
      return `${hours}:${minutes}`;
    },
    closeDialog() {
      this.internalVisible = false;
    },
    async fetchShiftTypes() {
      this.loading = true;
      try {
        const response = await this.$axios.get("/api/scheduling/shift-types", {
          params: { company_id: this.companyId },
        });
        this.shiftTypes = response.data.data || [];
        console.log(this.shiftTypes);
      } catch (error) {
        console.error("Error fetching shift types:", error);
        const errorMsg =
          error.response?.data?.message || error.message || "載入班表失敗";
        this.showSnackbar(errorMsg, "error");
      } finally {
        this.loading = false;
      }
    },
    openCreateDialog() {
      this.isEditing = false;
      this.newShift = {
        code: "",
        name: "",
        clock_in_time: "",
        clock_out_time: "",
        break_minutes: null,
        flexible: false,
        lunch_break: false,
        lunch_break_start: null,
        lunch_break_end: null,
        rest_1_start_enable: false,
        rest_1_start: null,
        rest_1_end: null,
        company_id: this.companyId,
      };
      this.createDialogVisible = true;
    },
    openEditDialog(shift) {
      this.isEditing = true;
      this.newShift = { ...shift };
      this.createDialogVisible = true;
    },
    closeCreateDialog() {
      this.createDialogVisible = false;
    },
    async saveShiftType() {
      if (!this.$refs.createForm.validate()) return;

      if (
        this.newShift.lunch_break &&
        (!this.newShift.lunch_break_start || !this.newShift.lunch_break_end)
      ) {
        this.showSnackbar("請填寫午休開始與結束時間", "error");
        return;
      }

      if (
        this.newShift.rest_1_start_enable &&
        (!this.newShift.rest_1_start || !this.newShift.rest_1_end)
      ) {
        this.showSnackbar("啟用休息2時，請填寫休息2開始與結束時間", "error");
        return;
      }

      const payload = { ...this.newShift };
      if (payload.clock_in_time && !payload.clock_in_time.includes(":"))
        payload.clock_in_time += ":00";
      if (payload.clock_out_time && !payload.clock_out_time.includes(":"))
        payload.clock_out_time += ":00";
      if (payload.lunch_break_start && !payload.lunch_break_start.includes(":"))
        payload.lunch_break_start += ":00";
      if (payload.lunch_break_end && !payload.lunch_break_end.includes(":"))
        payload.lunch_break_end += ":00";
      if (payload.rest_1_start && !payload.rest_1_start.includes(":"))
        payload.rest_1_start += ":00";
      if (payload.rest_1_end && !payload.rest_1_end.includes(":"))
        payload.rest_1_end += ":00";

      this.loading = true;
      try {
        if (this.isEditing) {
          await this.$axios.put(
            `/api/scheduling/shift-types/${payload.id}`,
            payload,
            {
              params: { company_id: this.companyId },
            }
          );
          this.showSnackbar("班表更新成功", "success");
        } else {
          await this.$axios.post("/api/scheduling/shift-types", payload, {
            params: { company_id: this.companyId },
          });
          this.showSnackbar("班表新增成功", "success");
        }
        this.closeCreateDialog();
        this.fetchShiftTypes();
        this.$emit(this.isEditing ? "updated" : "created");
      } catch (error) {
        console.error("Error saving shift type:", error);
        const errorMsg =
          error.response?.data?.message || error.message || "儲存班表失敗";
        this.showSnackbar(errorMsg, "error");
      } finally {
        this.loading = false;
      }
    },
    confirmDelete(id) {
      this.deleteId = id;
      this.deleteDialogVisible = true;
    },
    cancelDelete() {
      this.deleteDialogVisible = false;
      this.deleteId = null;
    },
    async deleteShiftType() {
      this.loading = true;
      try {
        await this.$axios.delete(
          `/api/scheduling/shift-types/${this.deleteId}`,
          {
            params: { company_id: this.companyId },
          }
        );
        this.showSnackbar("班表刪除成功", "success");
        this.cancelDelete();
        this.fetchShiftTypes();
        this.$emit("deleted");
      } catch (error) {
        console.error("Error deleting shift type:", error);
        const errorMsg =
          error.response?.data?.message || error.message || "刪除班表失敗";
        this.showSnackbar(errorMsg, "error");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
